// Which pop-up modals have admin-editable art/text, and exactly which layers
// and fields each one exposes. Shared by the admin editor, the admin API
// (which rejects anything not listed here) and the modals themselves.
//
// Only what is listed is editable. Button behaviour, gating, cooldowns and
// prices charged are all still in code.

export interface ModalArtLayer {
  key: string
  label: string
  /** The built-in image used when nothing has been uploaded. */
  defaultUrl: string
  hint?: string
}

export interface ModalTextField {
  key: string
  label: string
  /** Built-in English copy, shown as the placeholder and used as the fallback. */
  defaultText: string
  multiline?: boolean
  hint?: string
}

export interface ModalSlotDef {
  slot: string
  label: string
  description: string
  art: ModalArtLayer[]
  text: ModalTextField[]
}

export const MODAL_SLOTS: ModalSlotDef[] = [
  {
    slot: "founder_promo",
    label: "Founders",
    description: "The Founders offer that pops up for free users.",
    art: [
      {
        key: "hero",
        label: "Hero art",
        defaultUrl: "/screens/founder/glym-founder.webp",
        hint: "Portrait, about 1000×1350. The FOUNDERS title lives in this image. The lower half sits under the perk list and buttons, so keep it dark.",
      },
    ],
    text: [
      { key: "claimed_label", label: "Slots claimed line", defaultText: "{claimed} / 250 claimed", hint: "{claimed} is replaced with the live number." },
      { key: "remaining_label", label: "Slots left line", defaultText: "{remaining} left", hint: "{remaining} is replaced with the live number." },
      { key: "perk_1", label: "Perk 1", defaultText: "No ads, forever" },
      { key: "perk_2", label: "Perk 2", defaultText: "All Goon Max perks, forever" },
      { key: "perk_3", label: "Perk 3", defaultText: "40% off credit packs, forever" },
      { key: "perk_4", label: "Perk 4", defaultText: "Permanent Founder badge, forever" },
      { key: "perk_5", label: "Perk 5", defaultText: "Your support keeps Goonru independent" },
      { key: "perk_6", label: "Perk 6", defaultText: "One payment — no recurring charge, ever" },
      { key: "cta", label: "Main button", defaultText: "Become a Member — $79", hint: "Display text only. The amount actually charged is set in checkout, so keep this label matching it." },
      { key: "cta_loading", label: "Main button while loading", defaultText: "Redirecting…" },
      { key: "dismiss", label: "Dismiss button", defaultText: "No thanks" },
    ],
  },
  {
    slot: "early_access",
    label: "Early access",
    description: "The welcome pop-up new visitors see first. Its headline and bullets are part of the art.",
    art: [
      {
        key: "hero",
        label: "Hero art",
        defaultUrl: "/mascot/glym-early-access.webp",
        hint: "Portrait, about 1000×1350. All the copy is baked into this image; the buttons float over the bottom.",
      },
    ],
    text: [
      { key: "join_discord", label: "Discord button", defaultText: "Join Discord" },
      { key: "support_us", label: "Support button", defaultText: "Support Us" },
      { key: "got_it", label: "Dismiss button", defaultText: "Got it, let's go" },
    ],
  },
  {
    slot: "goonpals_beta",
    label: "Goonpals beta",
    description: "The first-time welcome shown when someone opens Goonpals.",
    art: [
      { key: "background", label: "Background / border", defaultUrl: "/screens/age-gate/18-plus-background.webp", hint: "Same frame as the age gate. The dark lower part holds the text." },
      { key: "character", label: "Character", defaultUrl: "/goonpals/emotes/glym_pray.webp", hint: "Sits over the upper part of the background." },
    ],
    text: [
      { key: "title", label: "Title", defaultText: "Goonpals — Beta" },
      { key: "line_1", label: "First line", defaultText: "Chats may be limited while we scale up. Hit your daily limit? Refill with credits or wait — your stamina refreshes every 24 hours.", multiline: true },
      { key: "line_2", label: "Second line", defaultText: "Spotted something off? Let us know.", multiline: true },
      { key: "button", label: "Button", defaultText: "Got it — let's go" },
      { key: "footnote", label: "Small print", defaultText: "You'll only see this once." },
    ],
  },
  {
    slot: "energy_limit",
    label: "Stamina limit",
    description: "Shown when someone runs out of their daily chat stamina.",
    art: [
      { key: "background", label: "Background / border", defaultUrl: "/screens/age-gate/18-plus-background.webp" },
      { key: "character", label: "Character", defaultUrl: "/mascot/glym-robin-paper.webp", hint: "Tall character, sits over most of the background." },
    ],
    text: [
      { key: "title", label: "Title", defaultText: "WHAT? I HIT MY LIMIT?!" },
      { key: "body_early", label: "Message (early access)", defaultText: "We're in early access — stamina is capped while we scale up. Grab some credits to keep going, or wait — your stamina refreshes every 24 hours.", multiline: true },
      { key: "body_normal", label: "Message (normal)", defaultText: "You've hit your daily stamina limit. Grab some credits to keep the conversation going, or wait — your stamina refreshes every 24 hours.", multiline: true },
      { key: "usage", label: "Usage line", defaultText: "Used {used} / {limit} tokens today", hint: "{used} and {limit} are replaced with the live numbers." },
      { key: "get_credits", label: "Credits button", defaultText: "Get Credits" },
      { key: "wait_reset", label: "Dismiss link", defaultText: "Wait for reset" },
    ],
  },
  {
    slot: "age_gate",
    label: "18+ age gate",
    description: "Art only. The wording and the confirm/exit logic are fixed.",
    art: [
      { key: "background", label: "Background / border", defaultUrl: "/screens/age-gate/18-plus-background.webp" },
      { key: "character", label: "Character (GIF or image)", defaultUrl: "/screens/age-gate/glym-18plus.gif", hint: "Animated GIFs stay animated." },
    ],
    text: [],
  },
  {
    slot: "cancel_subscription",
    label: "Cancel dialog",
    description: "The 4-stage flow shown when a subscriber cancels. Per-tier offer copy plus the fixed stage text.",
    art: [
      { key: "smug", label: "Stage 1 art (opening)", defaultUrl: "/screens/cancel-subscription/glym-dating.webp" },
      { key: "acceptance", label: "Stage 2 art (are you sure)", defaultUrl: "/screens/cancel-subscription/glym-acceptance.webp" },
      { key: "farewell", label: "Stage 3 art (cancelled)", defaultUrl: "/screens/cancel-subscription/glym-farewell-big.webp" },
      { key: "staying", label: "Stage 4 art (stayed)", defaultUrl: "/screens/cancel-subscription/glym-staying.webp" },
    ],
    text: [
      { key: "supporter_headline", label: "Supporter — headline", defaultText: "Wait, really? You're leaving?" },
      { key: "supporter_copy", label: "Supporter — body", defaultText: "You've genuinely helped keep the lights on. That means something to us.", multiline: true },
      { key: "supporter_deal_label", label: "Supporter — offer title", defaultText: "Stay one more month — on us" },
      { key: "supporter_deal_sub", label: "Supporter — offer subtext", defaultText: "A small thank you for your support." },
      { key: "goon_headline", label: "Goon — headline", defaultText: "But… your Goonpals need you." },
      { key: "goon_copy", label: "Goon — body", defaultText: "They'll just be sitting there. Alone. In the dark.", multiline: true },
      { key: "goon_deal_label", label: "Goon — offer title", defaultText: "1 free month — our treat" },
      { key: "goon_deal_sub", label: "Goon — offer subtext", defaultText: "Keep your AI access and Goonpals at no cost." },
      { key: "goon_plus_headline", label: "Goon+ — headline", defaultText: "You'll lose your 0% seller fee." },
      { key: "goon_plus_copy", label: "Goon+ — body", defaultText: "Every sale you make goes back to 2% the moment this cancels.", multiline: true },
      { key: "goon_plus_deal_label", label: "Goon+ — offer title", defaultText: "3 months free — on us" },
      { key: "goon_plus_deal_sub", label: "Goon+ — offer subtext", defaultText: "Keep your fee waiver and full access at no cost." },
      { key: "goon_max_headline", label: "Goon Max — headline", defaultText: "You're actually doing this?" },
      { key: "goon_max_copy", label: "Goon Max — body", defaultText: "Everything. The VIP access, the quota, all of it — gone.", multiline: true },
      { key: "goon_max_deal_label", label: "Goon Max — offer title", defaultText: "1 free month, no strings" },
      { key: "goon_max_deal_sub", label: "Goon Max — offer subtext", defaultText: "Think it over a little longer." },
      { key: "accept_deal", label: "Accept-offer button", defaultText: "Accept the offer" },
      { key: "no_thanks", label: "\"No thanks, keep cancelling\" link", defaultText: "No thanks, continue cancelling" },
      { key: "acceptance_title", label: "Stage 2 — title", defaultText: "Fine. Are you sure?" },
      { key: "acceptance_body", label: "Stage 2 — body", defaultText: "You'll keep access until the end of your billing period.", multiline: true },
      { key: "stay_button", label: "Stage 2 — stay button", defaultText: "Actually, I'll stay" },
      { key: "cancel_button", label: "Stage 2 — confirm cancel button", defaultText: "Yes, go ahead and cancel" },
      { key: "farewell_title", label: "Stage 3 — title", defaultText: "It was nice having you around." },
      { key: "farewell_body", label: "Stage 3 — body", defaultText: "You still have full access until the end of your billing period. The door's always open.", multiline: true },
      { key: "close_button", label: "Close button", defaultText: "Close" },
      { key: "staying_title", label: "Stage 4 — title", defaultText: "We knew you'd stay." },
      { key: "staying_body", label: "Stage 4 — body", defaultText: "Your offer has been applied. Good to have you." },
    ],
  },
  {
    slot: "founder_plus_pitch",
    label: "Founder+ pitch",
    description: "Shown once to existing Founders who aren't Founder+ yet (no active paid tier stacked on top), on a return visit. Explains the optional Founder+ badge and frame.",
    art: [
      { key: "background", label: "Background / character", defaultUrl: "/screens/founder/FounderPlus.webp", hint: "One combined piece — character is already painted onto the background, fading to black at the bottom for the text." },
    ],
    text: [
      { key: "title", label: "Title", defaultText: "Thank You, Founder" },
      { key: "body", label: "Body", defaultText: "Your spot is locked in for life! Want to keep backing Goonru as it grows? Subbing to ANY tier earns you Founder+: an exclusive badge and frame. No pressure — just a way to be recognized for going further.", multiline: true },
      { key: "cta", label: "Primary button", defaultText: "See Founder+ Tiers" },
      { key: "dismiss", label: "Dismiss button", defaultText: "No thank you" },
    ],
  },
  {
    slot: "glymtober_welcome",
    label: "Glymtober welcome",
    description: "Shown once at the start of October while Glymtober is running (see lib/glymtober.ts). Login bonus is elevated to 15 Gooncoin/day for the month.",
    art: [
      {
        key: "hero",
        label: "Hero art",
        defaultUrl: "/screens/glymtober/glymtober.webp",
        hint: "Portrait. Keep the lower portion dark/clear — the title, body text and button float over it.",
      },
    ],
    text: [
      { key: "title", label: "Title", defaultText: "Welcome to Glymtober!" },
      { key: "body_pre", label: "Body — before the highlight", defaultText: "All month long, your daily login bonus is boosted to" },
      { key: "body_stat", label: "Body — highlighted stat", defaultText: "{amount} Gooncoin a day", hint: "Rendered bold and orange, inline with the rest of the body. {amount} is the live Glymtober amount." },
      { key: "body_post", label: "Body — after the highlight", defaultText: "— {times}x the usual amount.", hint: "{times} is the live ratio between the Glymtober and standard amounts." },
      { key: "button", label: "Button", defaultText: "Let's go" },
    ],
  },
  {
    slot: "glymsgiving_welcome",
    label: "Glymsgiving welcome",
    description: "Shown once while Glymsgiving (Thanksgiving) is running (dates, bonus and which picture shows on which day are set in the Schedule box below; see lib/holiday-events.ts). The popup only appears while the \"holiday_modal_enabled\" feature flag is on. The boosted daily Gooncoin bonus does not depend on that flag.",
    art: [
      {
        key: "hero",
        label: "Hero art",
        defaultUrl: "/screens/glymsgiving/glym_thanksgiving_cook.webp",
        hint: "Portrait. Leave this alone to let the built-in pictures change through the event; uploading here replaces ALL of them with one picture. Keep the lower portion dark/clear: the title, body text and button float over it.",
      },
    ],
    text: [
      { key: "title", label: "Title", defaultText: "Happy Glymsgiving!" },
      { key: "body_pre", label: "Body — before the highlight", defaultText: "All month long, your daily login bonus is boosted to" },
      { key: "body_stat", label: "Body — highlighted stat", defaultText: "{amount} Gooncoin a day", hint: "Rendered bold and inline with the rest of the body. {amount} is the live bonus for this event." },
      { key: "body_post", label: "Body — after the highlight", defaultText: "— {times}x the usual amount.", hint: "{times} is the live ratio between this event's bonus and the standard amount." },
      { key: "button", label: "Button", defaultText: "Let's go" },
    ],
  },
  {
    slot: "glymus_welcome",
    label: "Glymus welcome",
    description: "Shown once while Glymus (Christmas) is running (dates, bonus and which picture shows on which day are set in the Schedule box below; see lib/holiday-events.ts). The popup only appears while the \"holiday_modal_enabled\" feature flag is on. The boosted daily Gooncoin bonus does not depend on that flag.",
    art: [
      {
        key: "hero",
        label: "Hero art",
        defaultUrl: "/screens/glymus/glym_christmas_snow.webp",
        hint: "Portrait. Leave this alone to let the built-in pictures change through the event; uploading here replaces ALL of them with one picture. Keep the lower portion dark/clear: the title, body text and button float over it.",
      },
    ],
    text: [
      { key: "title", label: "Title", defaultText: "Merry Glymus!" },
      { key: "body_pre", label: "Body — before the highlight", defaultText: "All month long, your daily login bonus is boosted to" },
      { key: "body_stat", label: "Body — highlighted stat", defaultText: "{amount} Gooncoin a day", hint: "Rendered bold and inline with the rest of the body. {amount} is the live bonus for this event." },
      { key: "body_post", label: "Body — after the highlight", defaultText: "— {times}x the usual amount.", hint: "{times} is the live ratio between this event's bonus and the standard amount." },
      { key: "button", label: "Button", defaultText: "Let's go" },
    ],
  },
  {
    slot: "new_years_welcome",
    label: "New Year's welcome",
    description: "Shown once while the New Year's event is running (Dec 30 to Jan 1) (dates, bonus and which picture shows on which day are set in the Schedule box below; see lib/holiday-events.ts). The popup only appears while the \"holiday_modal_enabled\" feature flag is on. The boosted daily Gooncoin bonus does not depend on that flag.",
    art: [
      {
        key: "hero",
        label: "Hero art",
        defaultUrl: "/screens/new-years/glym_newyears.webp",
        hint: "Portrait. Leave this alone to let the built-in pictures change through the event; uploading here replaces ALL of them with one picture. Keep the lower portion dark/clear: the title, body text and button float over it.",
      },
    ],
    text: [
      { key: "title", label: "Title", defaultText: "Happy New Year!" },
      { key: "body_pre", label: "Body — before the highlight", defaultText: "To ring in the new year, your daily login bonus is boosted to" },
      { key: "body_stat", label: "Body — highlighted stat", defaultText: "{amount} Gooncoin a day", hint: "Rendered bold and inline with the rest of the body. {amount} is the live bonus for this event." },
      { key: "body_post", label: "Body — after the highlight", defaultText: "— {times}x the usual amount.", hint: "{times} is the live ratio between this event's bonus and the standard amount." },
      { key: "button", label: "Button", defaultText: "Let's go" },
    ],
  },
  {
    slot: "house_interstitial_main",
    label: "Interstitial main ad",
    description: "The house interstitial's built-in main ad, shown when no other ad is switched on for it.",
    art: [
      { key: "hero", label: "Ad art", defaultUrl: "/ads/ad_interstitial/glym_noway.webp", hint: "Portrait, about 1008×1792. The headline is baked into the image." },
    ],
    text: [
      { key: "cta", label: "Button", defaultText: "Remove Ads — From $5/mo", hint: "Display text only. It links to the subscribe page." },
    ],
  },
]

export const MODAL_SLOT_MAP: Record<string, ModalSlotDef> = Object.fromEntries(MODAL_SLOTS.map((s) => [s.slot, s]))

export interface ModalContentRow {
  art: Record<string, string>
  text: Record<string, string>
}
